import { useMemo } from 'react';
import QRCode from 'qrcode';
import type { QrLayer } from '@bannermatic/core';

interface QrMatrix {
  size: number;
  /** Data modules (the three corner "eyes" are drawn apart). */
  path: string;
}

type DotStyle = NonNullable<QrLayer['dotStyle']>;
type EyeStyle = NonNullable<QrLayer['eyeStyle']>;

const inEye = (x: number, y: number, size: number) =>
  (x < 7 && y < 7) || (x >= size - 7 && y < 7) || (x < 7 && y >= size - 7);

/** Square module with each outer corner rounded where both neighbours around it are empty. */
function roundedModule(x: number, y: number, dark: (x: number, y: number) => boolean): string {
  const r = 0.5;
  const n = dark(x, y - 1);
  const s = dark(x, y + 1);
  const w = dark(x - 1, y);
  const e = dark(x + 1, y);
  const tl = !n && !w ? r : 0;
  const tr = !n && !e ? r : 0;
  const br = !s && !e ? r : 0;
  const bl = !s && !w ? r : 0;
  // A hair of overlap towards dark neighbours hides anti-aliasing seams
  const o = 0.02;
  const x0 = x - (w ? o : 0);
  const x1 = x + 1 + (e ? o : 0);
  const y0 = y - (n ? o : 0);
  const y1 = y + 1 + (s ? o : 0);
  return (
    `M${x0 + tl} ${y0}H${x1 - tr}` +
    (tr ? `A${tr} ${tr} 0 0 1 ${x1} ${y0 + tr}` : '') +
    `V${y1 - br}` +
    (br ? `A${br} ${br} 0 0 1 ${x1 - br} ${y1}` : '') +
    `H${x0 + bl}` +
    (bl ? `A${bl} ${bl} 0 0 1 ${x0} ${y1 - bl}` : '') +
    `V${y0 + tl}` +
    (tl ? `A${tl} ${tl} 0 0 1 ${x0 + tl} ${y0}` : '') +
    'Z'
  );
}

function buildMatrix(
  value: string,
  level: QrLayer['errorCorrection'],
  style: DotStyle,
  /** Modules covered by the logo (in module coordinates), left empty. */
  hole: number,
): QrMatrix | null {
  try {
    const qr = QRCode.create(value || ' ', { errorCorrectionLevel: level });
    const size = qr.modules.size;
    const data = qr.modules.data;
    const lo = (size - hole) / 2;
    const hi = (size + hole) / 2;
    const cut = (x: number, y: number) => hole > 0 && x + 1 > lo && x < hi && y + 1 > lo && y < hi;
    const dark = (x: number, y: number) =>
      x >= 0 &&
      y >= 0 &&
      x < size &&
      y < size &&
      Boolean(data[y * size + x]) &&
      !inEye(x, y, size) &&
      !cut(x, y);
    let path = '';
    if (style === 'square') {
      for (let y = 0; y < size; y++) {
        let runStart = -1;
        for (let x = 0; x <= size; x++) {
          const on = x < size && dark(x, y);
          if (on && runStart === -1) runStart = x;
          if (!on && runStart !== -1) {
            path += `M${runStart} ${y}h${x - runStart}v1h-${x - runStart}z`;
            runStart = -1;
          }
        }
      }
    } else {
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          if (!dark(x, y)) continue;
          if (style === 'dots') {
            const r = 0.42;
            path += `M${x + 0.5 - r} ${y + 0.5}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0z`;
          } else path += roundedModule(x, y, dark);
        }
      }
    }
    return { size, path };
  } catch {
    return null;
  }
}

function roundRect(x: number, y: number, w: number, h: number, r: number): string {
  if (r <= 0) return `M${x} ${y}h${w}v${h}h${-w}z`;
  return (
    `M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}` +
    `A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}` +
    `V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`
  );
}

const circle = (cx: number, cy: number, r: number) =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0z`;

/** The three corner squares: a 7×7 ring and a 3×3 centre. */
function eyePaths(size: number, style: EyeStyle): { ring: string; ball: string } {
  let ring = '';
  let ball = '';
  for (const [x, y] of [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ]) {
    if (style === 'circle') {
      ring += circle(x + 3.5, y + 3.5, 3.5) + circle(x + 3.5, y + 3.5, 2.5);
      ball += circle(x + 3.5, y + 3.5, 1.5);
    } else {
      const rounded = style === 'rounded';
      ring +=
        roundRect(x, y, 7, 7, rounded ? 2 : 0) + roundRect(x + 1, y + 1, 5, 5, rounded ? 1.4 : 0);
      ball += roundRect(x + 2, y + 2, 3, 3, rounded ? 0.9 : 0);
    }
  }
  return { ring, ball };
}

export function QrLayerView({
  layer,
  resolveSrc,
}: {
  layer: QrLayer;
  resolveSrc?: (src: string) => string;
}) {
  const dotStyle: DotStyle = layer.dotStyle ?? 'square';
  const eyeStyle: EyeStyle = layer.eyeStyle ?? 'square';
  const logo = layer.logo ?? '';
  const logoSize = layer.logoSize ?? 0.22;
  // A logo hides part of the code: the highest error correction keeps it readable
  const level = logo ? 'H' : layer.errorCorrection;
  const size = useMemo(() => {
    try {
      return QRCode.create(layer.value || ' ', { errorCorrectionLevel: level }).modules.size;
    } catch {
      return 0;
    }
  }, [layer.value, level]);
  // Logo box plus half a module of clearance, rounded up to whole modules on each side
  const hole = logo && size ? Math.min(size - 14, Math.ceil(size * logoSize + 1)) : 0;
  const matrix = useMemo(
    () => buildMatrix(layer.value, level, dotStyle, hole),
    [layer.value, level, dotStyle, hole],
  );
  const eyes = useMemo(() => (matrix ? eyePaths(matrix.size, eyeStyle) : null), [matrix, eyeStyle]);
  if (!matrix || !eyes)
    return (
      <div style={{ width: '100%', height: '100%', background: layer.background ?? undefined }} />
    );
  const m = layer.margin;
  const total = matrix.size + m * 2;
  const eyeColor = layer.eyeColor || layer.foreground;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', containerType: 'size' }}>
      <svg
        width="100%"
        height="100%"
        viewBox={`${-m} ${-m} ${total} ${total}`}
        preserveAspectRatio="xMidYMid meet"
        shapeRendering={dotStyle === 'square' && eyeStyle === 'square' ? 'crispEdges' : undefined}
        style={{ display: 'block' }}
      >
        {layer.background && (
          <rect x={-m} y={-m} width={total} height={total} fill={layer.background} />
        )}
        <path d={matrix.path} fill={layer.foreground} />
        <path d={eyes.ring} fill={eyeColor} fillRule="evenodd" />
        <path d={eyes.ball} fill={eyeColor} />
      </svg>
      {logo && (
        // An <img> (not an SVG <image>) so the renderer waits for it like for other pictures
        <img
          src={resolveSrc ? resolveSrc(logo) : logo}
          alt=""
          draggable={false}
          decoding="sync"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `calc(min(100cqw, 100cqh) * ${(matrix.size * logoSize) / total})`,
            height: `calc(min(100cqw, 100cqh) * ${(matrix.size * logoSize) / total})`,
            transform: 'translate(-50%, -50%)',
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}
